/**
 * COMPAT SHIM — temporary, and designed to be deleted. See `src/compat/README.md`
 * for the removal steps and the conditions under which it becomes dead weight.
 *
 * ## What is broken
 *
 * STForestKit hosts up to and including forest-ios 5.23.0 wrap every injected plugin
 * script in a per-document once-guard that records the script's id *before* running it:
 *
 * ```js
 * if (!window.STForestKit.executedScripts.has(id)) {
 *     window.STForestKit.executedScripts.add(id);   // marked first
 *     <script>;                                     // a throw here leaves the mark
 * }
 * ```
 *
 * On WebKit the host's console-logging plugin installs wrappers that are entered
 * without the scope of the block that declared their dependencies, so calling any
 * `console` method throws `ReferenceError`. The `BifrostRPC` plugin script's first
 * statement inside its guard is a `console.log`, so it dies there — before defining
 * either `window.Bifrost` or `window.bifrostNativeBridge` — with its id already
 * marked. The host's readiness watchdog then re-injects every 500ms and every
 * attempt short-circuits on the guard, so the bridge stays dead for the lifetime of
 * the document and this library's handshake can never complete.
 *
 * Fixed host-side in stforestkit#446 (marks after the script runs, and the console
 * wrappers no longer depend on call-time lexical state). That fix cannot be applied
 * retroactively to installed binaries, which is why this shim exists.
 *
 * ## What this does
 *
 * Both halves of the broken state live on this page's own `window` — the host
 * evaluates its scripts in the main world — so the page can repair it:
 *
 *   1. put a callable `console` back, so the RPC script's first statement survives;
 *   2. clear *only* the `BifrostRPC` mark, and let the host's own watchdog re-inject.
 *
 * We deliberately do not inject anything ourselves. The host's watchdog is already
 * running for exactly as long as the page is un-ready, and its injected script is
 * idempotent by construction (`if (!window.Bifrost)` / `if (window.Bifrost &&
 * !window.bifrostNativeBridge)`), so unblocking it is both sufficient and the
 * smallest possible intervention.
 *
 * ## Why this cannot fire on a healthy host
 *
 * The precondition is `mark present AND bridge absent`, which is reachable only on an
 * affected host. A fixed host records the mark only after the script has run, so if
 * the mark is there the bridge is there too and we return before touching anything.
 * A host that has not injected yet has no mark. A non-STForestKit host has no
 * `window.STForestKit`. In all three cases this is a no-op.
 */

import {
  installStForestKitConsoleGuard,
  pristineConsoleMethods,
} from "./StForestKitConsoleGuard";

declare global {
  interface Window {
    /**
     * Host-side once-guard bookkeeping. Present only on STForestKit hosts, and
     * private to the host — we read it here because the alternative is leaving
     * already-shipped binaries permanently broken.
     */
    STForestKit?: { executedScripts?: Set<string> };
    /** The Bifrost library object the host injects into the page. */
    Bifrost?: unknown;
  }
}

/** The host's plugin id for the script that installs the native bridge. */
const RPC_PLUGIN_ID = "BifrostRPC";

/**
 * Console methods the host's logging bridge replaces, and therefore the ones that
 * can be left throwing.
 */
const WRAPPED_METHODS = ["log", "debug", "info", "warn", "error"] as const;
type WrappedMethod = (typeof WRAPPED_METHODS)[number];
type ConsoleMethod = (...args: unknown[]) => void;

/**
 * Minimum spacing between attempts. The host watchdog re-injects on a 500ms tick, so
 * anything tighter would clear the mark again before the re-injection it is waiting
 * for has had a chance to run.
 */
const MIN_ATTEMPT_INTERVAL_MS = 750;

/**
 * Attempts allowed per document. A handful covers a re-injection that lands late or
 * throws once more; beyond that the failure is not the one this shim describes, and
 * repeatedly clearing the mark would keep the host re-running a script that has no
 * prospect of succeeding.
 */
const MAX_ATTEMPTS = 4;

const pristineConsole = pristineConsoleMethods();

// Installed at module load, before the host can replace `console`. This is the primary
// fix: with console calls unable to throw, the host's RPC script survives its opening
// `console.log` and installs the bridge on its first injection, and neither this library
// nor the host app can be thrown at from their own logging. Everything below is the
// remaining safety net for a host whose console could not be guarded.
installStForestKitConsoleGuard();

let attempts = 0;
let lastAttemptAt = 0;

/**
 * Whether calling `console.log` currently throws.
 *
 * Invoking it is the only way to find out — a poisoned wrapper is an ordinary callable
 * function and is indistinguishable by inspection. Reached only once the stuck
 * precondition already holds, so a healthy page never emits this.
 */
function consoleThrows(): boolean {
  try {
    // eslint-disable-next-line no-console -- probing callability is the point
    console.log();
    return false;
  } catch {
    return true;
  }
}

function assignConsole(methods: Partial<Record<WrappedMethod, ConsoleMethod>>) {
  for (const name of WRAPPED_METHODS) {
    const replacement = methods[name];
    if (replacement) {
      try {
        (console as unknown as Record<string, unknown>)[name] = replacement;
      } catch {
        // A host that made these non-writable is not one we can repair; the caller
        // still clears the mark, which is harmless, and the handshake still fails
        // on its own deadline rather than here.
      }
    }
  }
}

/**
 * Leave `console` callable.
 *
 * Restores the snapshot when it works, and neutralises to no-ops when it does not —
 * losing devtools output is a far smaller cost than leaving the RPC script unable to
 * execute its first statement.
 */
export function repairConsole(): "healthy" | "restored" | "neutralised" {
  if (!consoleThrows()) {
    return "healthy";
  }
  assignConsole(pristineConsole);
  if (!consoleThrows()) {
    return "restored";
  }
  const noop: ConsoleMethod = () => {
    /* nothing — better than throwing */
  };
  assignConsole({
    log: noop,
    debug: noop,
    info: noop,
    warn: noop,
    error: noop,
  });
  return "neutralised";
}

/**
 * Clear the host's stale `BifrostRPC` mark so its readiness watchdog can retry.
 *
 * Only these two ids are ever touched, and only `BifrostRPC` is cleared:
 *
 * - `ConsoleLoggingBridge` is left marked on purpose. Re-running it recreates the
 *   same broken lexical dependency and wraps an already-wrapped console. The cost is
 *   losing the host's console forwarding, which is host-side logging only.
 * - `MessageEventBridge` is left marked on purpose. On affected hosts its script does
 *   not remove a previously registered listener, so a re-injection stacks one and
 *   doubles every web→native message — duplicate analytics, duplicate URL opens.
 *   stforestkit#446's third change exists for precisely this reason.
 *
 * @returns true when a stale mark was cleared and a re-injection is now expected.
 */
export function attemptStForestKitOnceGuardRecovery(
  now: number = Date.now(),
): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  // The bridge is present, so whatever happened the host got there. Nothing to fix,
  // and this is the branch that keeps the shim off every fixed host.
  if (window.bifrostNativeBridge) {
    return false;
  }

  const marks = window.STForestKit?.executedScripts;
  if (!marks || typeof marks.delete !== "function") {
    return false; // not an STForestKit host, or the guard is not set up yet
  }
  // No mark means the host has not run the script yet — it is not stuck, it is early.
  if (!marks.has(RPC_PLUGIN_ID)) {
    return false;
  }

  if (
    attempts >= MAX_ATTEMPTS ||
    now - lastAttemptAt < MIN_ATTEMPT_INTERVAL_MS
  ) {
    return false;
  }
  attempts += 1;
  lastAttemptAt = now;

  repairConsole();
  try {
    marks.delete(RPC_PLUGIN_ID);
  } catch {
    return false;
  }
  return true;
}

/** Test seam: forget the per-document attempt budget. */
export function resetStForestKitOnceGuardRecoveryForTests(): void {
  attempts = 0;
  lastAttemptAt = 0;
}
